import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { DashletCard } from "@/components/DashletCard";
import { VerticalCard } from "@/components/VerticalCard";
import { Button } from "@/components/ui/button";
import { getDashboardStats, getVerticals, seedData } from "@/lib/api";
import { usePageTracking } from "@/hooks/useAuditLog";
import { ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function LandingPage() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Audit logging
  usePageTracking('Landing Page');

  useEffect(() => {
    const initData = async () => {
      try {
        // Try to seed data first
        await seedData().catch(() => {});
        
        const [statsRes, verticalsRes] = await Promise.all([
          getDashboardStats(),
          getVerticals()
        ]);
        setStats(statsRes.data);
        setVerticals(verticalsRes.data.slice(0, 6));
      } catch (error) {
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    initData();
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <SEOHead
        title="ERP Solutions & Digital Transformation for MSMEs"
        description="Absolin helps Indian MSMEs & SMEs transform with Odoo ERP, Focus X ERP, custom web apps, and AI consulting. 80+ live projects, 60+ experts. Free digital maturity assessment."
        keywords="ERP solutions India, Odoo ERP implementation, Focus X ERP, MSME digital transformation, SME ERP software, business digitalisation India, AI consulting MSME"
        canonicalUrl="/"
      />
      <Navbar />
      
      {/* Hero Section — redesigned for SEO + impact (21 industries, MSME/SME audience) */}
      <section className="relative pt-20 md:pt-24 overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
        {/* Decorative dotted grid */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle, #0066CC 1px, transparent 1px)",
          backgroundSize: "32px 32px"
        }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            {/* LEFT: Headline + CTA */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-[#0066CC] text-xs sm:text-sm font-semibold mb-5 sm:mb-6 animate-fade-in-up">
                <Sparkles className="h-3.5 w-3.5" />
                <span>Trusted ERP & Digital Transformation Partner since 2014</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] tracking-tight mb-5 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
                Run Your Business
                <span className="block">
                  Like the
                  <span className="relative inline-block mx-2">
                    <span className="relative z-10 text-[#0066CC]">Top 1%</span>
                    <span className="absolute bottom-1 left-0 right-0 h-3 bg-blue-100/70 -z-0 rounded" />
                  </span>
                  in Your Industry
                </span>
              </h1>

              <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-7 max-w-2xl animate-fade-in-up" style={{ animationDelay: "0.2s" }}>
                Absolin builds <strong className="text-slate-900">industry-specific ERP & software</strong> for Indian MSMEs and SMEs — across <strong className="text-slate-900">{(stats?.business_verticals || verticals.length || 21)} business verticals</strong>. Process-first consultations, 3-15 month rollouts depending on scope, and software your team will actually use.
              </p>

              <div className="flex flex-wrap gap-3 mb-8 animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
                <Link to="/verticals">
                  <Button size="lg" className="bg-[#0066CC] hover:bg-[#0052A3] text-white rounded-full px-7 sm:px-8 h-12 shadow-lg shadow-blue-200/50 btn-press" data-testid="explore-industries-btn">
                    Explore Your Industry <ArrowRight className="h-5 w-5 ml-2" />
                  </Button>
                </Link>
                <Button size="lg" variant="outline" className="rounded-full px-7 sm:px-8 h-12 border-slate-300 hover:border-[#0066CC] hover:bg-blue-50 btn-press" data-testid="search-solutions-btn"
                  onClick={() => { const fab = document.querySelector('[data-testid="ai-consultant-fab"]'); if (fab) fab.click(); }}>
                  Talk to AI Consultant
                </Button>
              </div>

              {/* Trust strip */}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs sm:text-sm text-slate-500 animate-fade-in-up" style={{ animationDelay: "0.4s" }}>
                <div className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" /> 80+ Live Implementations</div>
                <div className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" /> 60+ Functional Experts</div>
                <div className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" /> Visakhapatnam · Hyderabad · Bengaluru</div>
              </div>
            </div>

            {/* RIGHT: Visual — stacked impact cards */}
            <div className="lg:col-span-5 relative animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {/* Card 1 */}
                <div className="col-span-2 bg-gradient-to-br from-[#0066CC] to-[#0052A3] rounded-2xl p-5 sm:p-6 text-white shadow-xl shadow-blue-200/40">
                  <p className="text-[11px] uppercase tracking-wider opacity-80 font-semibold">Industries Served</p>
                  <div className="flex items-end justify-between mt-1">
                    <p className="text-5xl sm:text-6xl font-bold tracking-tight">{stats?.business_verticals || verticals.length || 21}</p>
                    <p className="text-xs opacity-80 max-w-[140px] text-right">From dairy & retail to pharma, education & shipbuilding</p>
                  </div>
                </div>
                {/* Card 2 */}
                <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-200 shadow-sm">
                  <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Problem Solutions</p>
                  <p className="text-3xl sm:text-4xl font-bold text-slate-900 mt-1">{stats?.total_problems || 500}+</p>
                  <p className="text-[11px] text-slate-500 mt-1">Addressed & Implemented by ABSOLIN Functional Consultants</p>
                </div>
                {/* Card 3 */}
                <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-200 shadow-sm">
                  <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Avg. Rollout</p>
                  <p className="text-3xl sm:text-4xl font-bold text-slate-900 mt-1">3-15<span className="text-base sm:text-lg ml-1 text-slate-500">mo</span></p>
                  <p className="text-[11px] text-slate-500 mt-1">scope-based · vs 12-18 mo for big-vendor ERPs</p>
                </div>
                {/* Card 4 */}
                <div className="col-span-2 bg-slate-900 rounded-2xl p-5 text-white">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <p className="text-[11px] uppercase tracking-wider opacity-70 font-semibold">Investment Range</p>
                      <p className="text-2xl sm:text-3xl font-bold mt-0.5">₹15L – ₹2Cr</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[11px] uppercase tracking-wider opacity-70 font-semibold">Typical Payback</p>
                      <p className="text-2xl sm:text-3xl font-bold mt-0.5">9-14 mo</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dashboard Metrics */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('landing.ourImpact')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t('landing.ourImpactDescription')}
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-32 rounded-2xl bg-white animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
              <DashletCard 
                icon="LayoutDashboard"
                label={t('landing.businessVerticals')}
                value={stats?.business_verticals || 21}
                color="#0066CC"
              />
              <DashletCard 
                icon="Briefcase"
                label={t('landing.liveProjects')}
                value={stats?.live_projects || 80}
                color="#16A34A"
              />
              <DashletCard 
                icon="Users"
                label={t('landing.technicalExperts')}
                value={stats?.total_technical_experts || 60}
                expandable={true}
                color="#D97706"
                expandedContent={
                  <div className="space-y-3">
                    {stats?.expert_departments?.map((dept) => (
                      <div key={dept.id} className="flex justify-between items-center text-sm">
                        <span className="text-slate-600">{dept.department}</span>
                        <span className="font-semibold text-slate-900">{dept.count}</span>
                      </div>
                    ))}
                  </div>
                }
              />
              <DashletCard 
                icon="MapPin"
                label={t('landing.presenceBranches')}
                value={stats?.branches || 3}
                expandable={true}
                color="#9333EA"
                expandedContent={
                  <div className="space-y-4">
                    {stats?.branch_details?.map((branch) => (
                      <div key={branch.id} className="text-sm">
                        <p className="font-semibold text-slate-900">{branch.name}</p>
                        <p className="text-slate-500">{branch.address}, {branch.city}</p>
                      </div>
                    ))}
                  </div>
                }
              />
            </div>
          )}
        </div>
      </section>

      {/* Featured Verticals */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {t('landing.industrySolutions')}
              </h2>
              <p className="text-slate-600 max-w-2xl">
                {t('landing.industrySolutionsDesc')}
              </p>
            </div>
            <Link to="/verticals">
              <Button 
                variant="outline" 
                className="rounded-full border-slate-200 hover:border-[#0066CC] hover:bg-[#E6F0FA]"
                data-testid="view-all-verticals-btn"
              >
                {t('landing.viewAllIndustries')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-80 rounded-2xl bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 stagger-children">
              {verticals.map((vertical) => (
                <VerticalCard key={vertical.id} vertical={vertical} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-[#0066CC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('landing.readyToTransform')}
          </h2>
          <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
            {t('landing.readyToTransformDesc')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://www.absolinsoft.com/contact-us/" target="_blank" rel="noopener noreferrer">
              <Button 
                size="lg" 
                className="bg-white text-[#0066CC] hover:bg-blue-50 rounded-full px-8 btn-press"
                data-testid="contact-us-btn"
              >
                {t('common.contactUs')}
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </a>
            <Button 
              size="lg" 
              variant="outline"
              className="rounded-full px-8 border-white/30 text-white hover:bg-white/10 btn-press"
              onClick={() => {
                const fab = document.querySelector('[data-testid="ai-consultant-fab"]');
                if (fab) fab.click();
              }}
            >
              {t('common.askAIConsultant')}
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
